import { useRef, useState, type FormEvent } from "react";
import { useAction, useConvex, useMutation, usePaginatedQuery, useQuery } from "convex/react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Download, FileSpreadsheet, Radar, Trash2, Upload } from "lucide-react";
import { api } from "../../convex/_generated/api";
import { WlsLogo } from "../components/WlsLogo";
import type { Id } from "../../convex/_generated/dataModel";
import { Badge, Button, Card, ConfirmDialog, EmptyState, Field, Input, Notice, PageHeader, TableSkeleton } from "../components/ui";
import { STATUS_LABELS, errorMessage, formatDate } from "../lib/format";
import { downloadDiscordIds } from "../lib/banTools";
import { BanReasonPicker } from "../components/BanReasonPicker";

export function ChecksPage() {
  const me = useQuery(api.users.me);
  const convex = useConvex();
  const navigate = useNavigate();
  const runCsv = useAction(api.checks.runCsv);
  const runWls = useAction(api.checks.runWlsTournament);
  const { results, status, loadMore } = usePaginatedQuery(api.checks.list, {}, { initialNumItems: 20 });
  const [source, setSource] = useState<"wls" | "csv">("csv");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ tone: "error" | "success"; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const removeCheck = useMutation(api.checks.remove);
  const [deleting, setDeleting] = useState<{ id: Id<"tournamentChecks">; label: string } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [exporting, setExporting] = useState<Id<"tournamentChecks"> | null>(null);
  // L'analyse tourne côté serveur : on rejoint immédiatement sa fiche, qui affiche l'avancement
  // en direct. Quitter la page ne fait donc plus perdre le résultat.
  const submitWls = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setLoading(true); setMessage(null);
    const form = new FormData(event.currentTarget);
    try {
      // Les groupes du tournoi sont détectés automatiquement côté serveur.
      const result = await runWls({ tournamentId: String(form.get("tournamentId")) });
      navigate(`/controles/${result.checkId}`);
    } catch (reason) { setMessage({ tone: "error", text: errorMessage(reason) }); setLoading(false); }
  };
  /**
   * Choisir le fichier vaut lancement : c'est la seule chose que l'on puisse vouloir en faire.
   * Le champ est vidé aussitôt lu, sinon resélectionner le même fichier n'émettrait plus
   * d'événement et l'analyse ne repartirait pas après une erreur.
   */
  const analyserCsv = async (file: File) => {
    setLoading(true); setMessage(null);
    try {
      const result = await runCsv({ fileName: file.name, csvText: await file.text() });
      if (fileRef.current) fileRef.current.value = "";
      navigate(`/controles/${result.checkId}`);
    } catch (reason) {
      if (fileRef.current) fileRef.current.value = "";
      setMessage({ tone: "error", text: errorMessage(reason) });
      setLoading(false);
    }
  };

  const exportDiscord = async (checkId: Id<"tournamentChecks">) => {
    setExporting(checkId); setMessage(null);
    try {
      const result = await convex.query(api.checks.exportDiscordIds, { checkId });
      const count = downloadDiscordIds(result.label, result.ids);
      setMessage(count > 0
        ? { tone: "success", text: `${count} ID Discord exporté${count > 1 ? "s" : ""}.` }
        : { tone: "error", text: "Aucun ID Discord exploitable dans cette analyse." });
    } catch (reason) { setMessage({ tone: "error", text: errorMessage(reason) }); }
    finally { setExporting(null); }
  };
  return <>
    <PageHeader title="Contrôles de tournoi" description="Comparez les inscrits à la blacklist. Chaque rapprochement reste une piste à valider humainement." />
    {me?.role === "admin" && <Card className="check-launcher"><div className="source-switch" role="group" aria-label="Source des inscrits"><button type="button" aria-pressed={source === "wls"} className={source === "wls" ? "active" : ""} onClick={() => setSource("wls")}><WlsLogo size={20} /><span><strong>Tournoi WLS</strong><small>Depuis l’API officielle</small></span></button><button type="button" aria-pressed={source === "csv"} className={source === "csv" ? "active" : ""} onClick={() => setSource("csv")}><FileSpreadsheet size={18} /><span><strong>Fichier CSV</strong><small>Export d’inscriptions</small></span></button></div>
      {source === "wls" ? <form onSubmit={submitWls} className="inline-form inline-form--csv"><Field label="Identifiant du tournoi" hint="L’identifiant du classement WLS, lu sans jeton."><Input name="tournamentId" required placeholder="Ex. 858500911385833472" /></Field><Button type="submit" loading={loading}><Radar size={16} /> Analyser</Button></form> : <label
        className={`dropzone${dragOver ? " dropzone--over" : ""}${loading ? " dropzone--busy" : ""}`}
        onDragOver={(event) => { event.preventDefault(); if (!loading) setDragOver(true); }}
        onDragEnter={(event) => { event.preventDefault(); if (!loading) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          if (loading) return;
          const file = event.dataTransfer.files?.[0];
          if (!file) return;
          // Un fichier déposé n'est filtré par rien : le champ `accept` ne vaut que pour le
          // sélecteur. Sans ce contrôle, un PDF partirait à l'analyse et échouerait plus loin.
          if (!/\.csv$/i.test(file.name) && file.type !== "text/csv") {
            setMessage({ tone: "error", text: `« ${file.name} » n’est pas un fichier CSV.` });
            return;
          }
          void analyserCsv(file);
        }}
      >
        <input
          ref={fileRef}
          name="csv"
          type="file"
          accept=".csv,text/csv"
          disabled={loading}
          className="dropzone__input"
          onChange={(event) => { const file = event.target.files?.[0]; if (file) void analyserCsv(file); }}
        />
        <span className="dropzone__icon"><Upload size={22} aria-hidden="true" /></span>
        {loading ? (
          <span className="dropzone__copy"><strong>Import et analyse en cours…</strong><small>Vous pouvez quitter cette page, l’analyse se poursuit.</small></span>
        ) : (
          <span className="dropzone__copy">
            <strong>Déposez l’export CSV des inscriptions</strong>
            <small>ou cliquez pour le choisir — l’analyse démarre aussitôt</small>
          </span>
        )}
      </label>}
    </Card>}
    {message && <Notice tone={message.tone}>{message.text}</Notice>}
    <div className="section-heading"><h2>Analyses enregistrées</h2><BanReasonPicker /></div>
    {status === "LoadingFirstPage" ? <TableSkeleton /> : results.length === 0 ? <EmptyState title="Aucun contrôle" description="Lancez une première analyse depuis un tournoi WLS ou un export CSV." /> : <Card className="check-list">
      {results.map((check) => {
        const label = check.tournamentId || check.fileName || "Contrôle sans nom";
        return (
          <div key={check._id} className="check-row check-row--deletable">
            <Link to={`/controles/${check._id}`} className="check-row__link">
              <span className="check-row__source" title={check.source === "csv" ? "Import CSV" : "Tournoi WLS"}>{check.source === "csv" ? "CSV" : <WlsLogo size={20} />}</span>
              <span className="check-row__main"><strong>{label}</strong><small>{formatDate(check.createdAt)}</small></span>
              <span className="check-row__metrics"><strong>{check.processedRegistrants}/{check.totalRegistrants}</strong><small>joueurs traités</small></span>
              <span className="check-row__metrics"><strong>{check.suspectsFound}</strong><small>détections</small></span>
              <Badge tone={check.status === "completed" ? "success" : check.status === "failed" ? "danger" : "warning"}>{STATUS_LABELS[check.status]}</Badge>
              <ArrowRight size={17} />
            </Link>
            <Button
              variant="ghost" className="button--icon button--icon-neutral" loading={exporting === check._id}
              aria-label={`Exporter les IDs Discord de ${label}`} title="Exporter les IDs Discord"
              onClick={() => void exportDiscord(check._id)}
            >
              <Download size={16} />
            </Button>
            {me?.role === "admin" && (
              <Button variant="ghost" className="button--icon" aria-label={`Supprimer l’analyse ${label}`} onClick={() => setDeleting({ id: check._id, label })}>
                <Trash2 size={16} />
              </Button>
            )}
          </div>
        );
      })}
      {status === "CanLoadMore" && <div className="load-more"><Button variant="secondary" onClick={() => loadMore(20)}>Afficher plus</Button></div>}
    </Card>}
    {deleting && (
      <ConfirmDialog
        title="Supprimer cette analyse ?"
        description="L’analyse, ses inscrits et ses détections sont effacés. Les entrées de blacklist qu’elle a créées ne sont pas touchées."
        target={deleting.label}
        confirmLabel="Supprimer l’analyse"
        onConfirm={() => removeCheck({ checkId: deleting.id })}
        onClose={() => setDeleting(null)}
      />
    )}
  </>;
}
